import { useEffect, useState, useSyncExternalStore } from 'react'
import { useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { keys } from '../../../api/keys'
import { hasChosenLanguage } from '../../../app/language'
import { useSession } from '../../../app/session'
import { env } from '../../../config/env'
import { anySheetOpen, subscribeSheets } from '../../../ui/openSheets'
import { useDismissReview, usePendingReviews } from '../api'
import type { ReviewTarget } from '../model'
import { ReviewSheet } from './ReviewSheet'

/** Где не спрашиваем: на оформлении покупатель занят заказом (HIG ratings-and-reviews.md). */
const QUIET_PATHS = ['/checkout']

/**
 * Сама спрашивает оценку закрытого заказа — один раз на заказ. Живёт в
 * каркасе приложения: вход и оформление заказа вне его или в тишине.
 * Только при флаге `reviews` и только у вошедшего покупателя.
 */
export function ReviewPrompt() {
  const { isAuthenticated } = useSession()
  if (!env.features.reviews || !isAuthenticated) return null
  return <Prompt />
}

/**
 * Берёт самый свежий заказ из ждущих оценки и открывает шторку. Закрыли без
 * оценки — «не сейчас»: об этом заказе больше не спрашиваем, оценить его
 * можно из карточки заказа.
 *
 * Не надоедаем (HIG ratings-and-reviews.md › Avoid pestering people): за
 * заход в приложение — не больше одной шторки. Заход — запуск и каждое
 * возвращение на экран; на возвращении список перечитываем: заказ могли
 * выдать, пока покупатель был в другом приложении. Поверх чужой шторки и
 * приветствия с выбором языка не открываемся — ждём, пока они уйдут.
 */
function Prompt() {
  const { pathname } = useLocation()
  const client = useQueryClient()
  const pending = usePendingReviews(true)
  const dismiss = useDismissReview()
  const busy = useSyncExternalStore(subscribeSheets, anySheetOpen)
  // Последний заказ, о котором спросили: держим и после закрытия, пока шторка уезжает.
  const [target, setTarget] = useState<ReviewTarget | null>(null)
  const [opened, setOpened] = useState(false)
  const [visit, setVisit] = useState(0)
  const [askedVisit, setAskedVisit] = useState(-1)
  // О каких заказах уже спросили: «не сейчас» могло не дойти до сервера.
  const [asked, setAsked] = useState<number[]>([])

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      setVisit((n) => n + 1)
      // На mock ожидающие считаются из списка заказов — перечитываем его.
      if (env.reviewsMock) void client.invalidateQueries({ queryKey: keys.orders })
      void client.invalidateQueries({ queryKey: keys.reviewsPending })
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [client])

  const next = pending.data?.find((item) => !asked.includes(item.orderId))
  const quiet = QUIET_PATHS.some((path) => pathname.startsWith(path))

  useEffect(() => {
    if (!next || opened || askedVisit === visit || quiet || busy || !hasChosenLanguage()) return
    setTarget(next)
    setOpened(true)
    setAskedVisit(visit)
    setAsked((ids) => [...ids, next.orderId])
  }, [next, opened, askedVisit, visit, quiet, busy])

  if (!target) return null
  return (
    <ReviewSheet
      opened={opened}
      target={target}
      onClose={(rated) => {
        if (!rated) dismiss.mutate(target.orderId)
        setOpened(false)
      }}
    />
  )
}
